import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { MessageSquarePlus, X } from 'lucide-react';
import { useChangelog } from '../../hooks/useChangelog';

export default function FeedbackFAB() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const location = useLocation();
  const { submitIssue } = useChangelog();

  const resetForm = () => {
    setType('bug');
    setTitle('');
    setDescription('');
    setSuccess(null);
    setError(null);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const result = await submitIssue({
        title,
        description,
        type,
        page: location.pathname,
      });
      setSuccess(`${type === 'bug' ? 'Bug report' : 'Feature request'} submitted (${result.issueIdentifier}). We'll email you when it's picked up and when it's done.`);
      setTitle('');
      setDescription('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <>
      {/* FAB button - bottom right, hidden on small screens where it would overlap content */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 hidden sm:flex items-center gap-2 px-4 py-3 bg-activ-navy text-white text-sm font-medium rounded-full shadow-lg hover:bg-activ-navy/90 transition-all hover:shadow-xl mb-[env(safe-area-inset-bottom)]"
          aria-label="Report bug or request feature"
        >
          <MessageSquarePlus size={18} />
          <span className="hidden md:inline">Feedback</span>
        </button>
      )}

      {/* Modal overlay + panel */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-end justify-end" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/20" onClick={handleClose} />

          {/* Panel - anchored bottom-right on desktop */}
          <div className="relative z-10 w-full sm:w-[420px] sm:m-6 bg-white rounded-t-xl sm:rounded-ae-sm shadow-2xl max-h-[85vh] overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)] sm:pb-0">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-activ-navy">Report Bug / Request Feature</h3>
              <button
                onClick={handleClose}
                className="p-2 -mr-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Type selector */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setType('bug')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                    type === 'bug'
                      ? 'bg-red-50 border-red-300 text-red-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Bug Report
                </button>
                <button
                  type="button"
                  onClick={() => setType('feature')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                    type === 'feature'
                      ? 'bg-teal-50 border-teal-300 text-teal-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Feature Request
                </button>
              </div>

              {/* Title */}
              <div>
                <label htmlFor="fab-issue-title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  id="fab-issue-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  placeholder={type === 'bug' ? 'Brief description of the issue' : 'What would you like to see?'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-activ-teal focus:border-transparent"
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="fab-issue-desc" className="block text-sm font-medium text-gray-700 mb-1">
                  Details <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="fab-issue-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder={type === 'bug' ? 'Steps to reproduce, what you expected, what happened instead...' : 'Describe the feature, why it would be useful...'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-activ-teal focus:border-transparent resize-vertical"
                />
              </div>

              {/* Page context indicator */}
              <p className="text-xs text-gray-400">
                Submitted from: {location.pathname}
              </p>

              {/* Submit */}
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={submitting || !title.trim()}
                  className="px-4 py-2 bg-activ-teal text-white text-sm font-medium rounded-lg hover:bg-activ-navy transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
                {success && (
                  <span className="text-sm text-crm-success-text font-medium">{success}</span>
                )}
                {error && (
                  <span className="text-sm text-red-600">{error}</span>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}
